import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { DocumentTextIcon } from '@heroicons/react/24/outline';

interface GalleryImage {
  key: string;
  fileName: string;
  url: string;
  size: number;
  lastModified: string;
  caption?: string;
}

const Gallery: React.FC = () => {
  const [folders, setFolders] = useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadFolders();
  }, []);

  useEffect(() => {
    loadImages();
  }, [selectedFolder]);

  const loadFolders = async () => {
    try {
      const folderList = await apiService.getGalleryFolders();
      setFolders(folderList);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  const loadImages = async () => {
    setLoading(true);
    try {
      const imageList = await apiService.getGalleryImages(selectedFolder || undefined);
      setImages(imageList);
    } catch (error) {
      console.error('Failed to load images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (image: GalleryImage) => {
    const index = images.findIndex(img => img.key === image.key);
    setCurrentImageIndex(index);
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : images.length - 1;
    setCurrentImageIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentImageIndex < images.length - 1 ? currentImageIndex + 1 : 0;
    setCurrentImageIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    }
  };

  return (
    <div className="min-h-screen bg-church-light py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-serif font-bold text-church-dark mb-4">
            Parish Gallery
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Capturing moments of faith, fellowship, and community life at All Saints Catholic Church
          </p>
        </header>

        {/* Folder tabs */}
        <div className="mb-8">
          <nav className="flex space-x-1 overflow-x-auto">
            <button
              onClick={() => setSelectedFolder('')}
              className={`px-6 py-3 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors duration-200 ${
                selectedFolder === ''
                  ? 'bg-white text-church-blue border-b-2 border-church-blue'
                  : 'bg-gray-100 text-gray-600 hover:text-church-blue hover:bg-gray-200'
              }`}
            >
              All Photos
            </button>
            {folders.map((folder) => (
              <button
                key={folder}
                onClick={() => setSelectedFolder(folder)}
                className={`px-6 py-3 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors duration-200 ${
                  selectedFolder === folder
                    ? 'bg-white text-church-blue border-b-2 border-church-blue'
                    : 'bg-gray-100 text-gray-600 hover:text-church-blue hover:bg-gray-200'
                }`}
              >
                {folder.charAt(0).toUpperCase() + folder.slice(1).replace(/-/g, ' ')}
              </button>
            ))}
          </nav>
        </div>

        {/* Images section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-serif font-semibold text-church-dark mb-6">
            {selectedFolder 
              ? `${selectedFolder.charAt(0).toUpperCase() + selectedFolder.slice(1).replace(/-/g, ' ')} (${images.length})`
              : `All Photos (${images.length})`
            }
          </h2>

          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading images...</p>
            </div>
          ) : images.length === 0 ? (
            selectedFolder === '' && folders.length > 0 ? (
              <div className="py-8">
                <p className="text-gray-600 text-center mb-6">
                  Select a folder below to explore the different photo collections.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {folders.map((folder) => (
                    <button
                      key={folder}
                      onClick={() => setSelectedFolder(folder)}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-left shadow-sm hover:shadow-md transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue"
                    >
                      <div className="flex items-center">
                        <span className="flex-shrink-0">
                          <DocumentTextIcon className="h-8 w-8 text-church-blue" />
                        </span>
                        <span className="ml-4 text-lg font-medium text-church-dark">
                          {folder.charAt(0).toUpperCase() + folder.slice(1).replace(/-/g, ' ')}
                        </span>
                      </div>
                      <p className="mt-3 text-sm text-gray-500">
                        Tap to view photos from this gallery collection.
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">No images available in this collection.</p>
                <p className="text-gray-400 mt-2">Check back soon for new photos!</p>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {images.map((image) => (
                <div
                  key={image.key}
                  className="group cursor-pointer relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                  onClick={() => openLightbox(image)}
                >
                  <div className="aspect-square">
                    <img
                      src={image.url}
                      alt={image.caption || image.fileName}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Overlay with caption */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      {image.caption && (
                        <p className="text-white text-sm font-medium line-clamp-2">
                          {image.caption}
                        </p>
                      )}
                      <p className="text-gray-300 text-xs mt-1">
                        {new Date(image.lastModified).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closeLightbox}
            onKeyDown={handleKeyPress}
            tabIndex={0}
          >
            <div className="relative max-w-7xl max-h-full mx-4">
              {/* Close button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 text-4xl font-light"
                aria-label="Close lightbox"
              >
                ×
              </button>

              {/* Navigation buttons */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-6xl font-light z-10"
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToNext(); }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-6xl font-light z-10"
                    aria-label="Next image"
                  >
                    ›
                  </button>
                </>
              )}

              {/* Image */}
              <div 
                className="flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage.url}
                  alt={selectedImage.caption || selectedImage.fileName}
                  className="max-w-full max-h-[90vh] object-contain"
                />
              </div>

              {/* Caption */}
              {selectedImage.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white text-lg font-medium text-center">
                    {selectedImage.caption}
                  </p>
                  <p className="text-gray-300 text-sm text-center mt-2">
                    {new Date(selectedImage.lastModified).toLocaleDateString()} • 
                    Image {currentImageIndex + 1} of {images.length}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
